import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useDelete,
  useGetList,
} from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { Calendar, User, MessageSquare, Flag, Trash2 } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Stars, AvatarName, RowActions } from '../components/ui/data-cells';
import { toastSuccess, toastError, toastInfo } from '../lib/toast';
import { cn } from '../lib/utils';

/* ============================================================
   Session reviews — Latio design system (DESIGN_SYSTEM.md §6/§7).
   List (stars + excerpt + inline Keep/Remove) + full-page detail
   (quoted text + flag callout). Remove → confirm + delete; Keep → toast.
   Low-rated reviews (≤2★) are surfaced as "flagged" for moderation.
   ============================================================ */

interface SessionReview {
  id: number;
  rating: number;
  review_type: 'user_to_trainer' | 'trainer_to_user' | string;
  comment?: string;
  session_id: number;
  session_title?: string;
  reviewer_name?: string;
  reviewer_email?: string;
  reviewed_user_name?: string;
  reviewed_user_email?: string;
  created_at?: string;
}

const TYPE_LABEL: Record<string, string> = {
  user_to_trainer: 'Athlete → Coach',
  trainer_to_user: 'Coach → Athlete',
};
const typeLabel = (t?: string) => (t ? TYPE_LABEL[t] || t.replace(/_/g, ' ') : '—');
const isFlagged = (rating?: number) => (rating ?? 5) <= 2;

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/* ---- List config ---- */
const REVIEW_TABS = [
  { value: 'all', label: 'All' },
  { value: 'user_to_trainer', label: 'Athlete → Coach' },
  { value: 'trainer_to_user', label: 'Coach → Athlete' },
];
const REVIEW_SORT = [
  { field: 'created_at', label: 'Date' },
  { field: 'rating', label: 'Rating' },
  { field: 'id', label: 'ID' },
];
const REVIEW_SORT_LABELS = Object.fromEntries(REVIEW_SORT.map((f) => [f.field, f.label]));

const ReviewTypeTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.review_type as string) || 'all';
  return (
    <SegmentedTabs
      tabs={REVIEW_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, review_type: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const ReviewStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const all = useGetList('session-reviews', { ...base, filter: {} });
  const toCoach = useGetList('session-reviews', { ...base, filter: { review_type: 'user_to_trainer' } });
  const toAthlete = useGetList('session-reviews', { ...base, filter: { review_type: 'trainer_to_user' } });
  const flagged = useGetList('session-reviews', { ...base, filter: { max_rating: 2 } });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Total reviews" value={n(all.total)} />
      <StatCard label="Athlete → Coach" value={n(toCoach.total)} />
      <StatCard label="Coach → Athlete" value={n(toAthlete.total)} />
      <StatCard label="Flagged (≤2★)" value={n(flagged.total)} />
    </StatRow>
  );
};

/* ---- Inline Keep / Remove ---- */
function InlineModeration({ review }: { review: SessionReview }) {
  const [deleteOne] = useDelete();
  const [removeOpen, setRemoveOpen] = React.useState(false);

  const remove = () =>
    deleteOne(
      'session-reviews',
      { id: review.id, previousData: review },
      {
        onSuccess: () => toastSuccess('Review removed'),
        onError: (e: any) => toastError(e?.message || 'Failed to remove review'),
        mutationMode: 'pessimistic',
      },
    );

  return (
    <RowActions>
      <button
        type="button"
        onClick={() => toastInfo('Review kept')}
        className="inline-flex h-8 items-center rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink hover:bg-surface-2"
      >
        Keep
      </button>
      <button
        type="button"
        onClick={() => setRemoveOpen(true)}
        title="Remove review"
        className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error"
      >
        <Trash2 className="size-4" />
      </button>
      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        tone="destructive"
        title={`Remove review #${review.id}?`}
        consequence="This permanently removes the review. This cannot be undone."
        confirmLabel="Remove review"
        onConfirm={remove}
      />
    </RowActions>
  );
}

const ReviewsListBody = () => {
  const columns: DataColumn<SessionReview>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'rating',
      header: 'Review',
      sortable: true,
      cell: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Stars value={r.rating} />
            {isFlagged(r.rating) && (
              <span className="inline-flex items-center gap-1 rounded-pill bg-error-bg px-2 py-0.5 text-[11px] font-semibold text-error">
                <Flag className="size-3" /> Flagged
              </span>
            )}
          </div>
          {r.comment && <div className="mt-0.5 max-w-[320px] truncate text-[13px] text-ink-2">"{r.comment}"</div>}
        </div>
      ),
    },
    { key: 'type', header: 'Type', cell: (r) => <span className="text-ink-2">{typeLabel(r.review_type)}</span> },
    {
      key: 'session',
      header: 'Class',
      cell: (r) => <span className="text-ink">{r.session_title || `#${r.session_id}`}</span>,
    },
    {
      key: 'reviewer',
      header: 'Reviewer',
      cell: (r) => (r.reviewer_name ? <AvatarName name={r.reviewer_name} subtext={r.reviewed_user_name ? `→ ${r.reviewed_user_name}` : undefined} size={28} /> : <span className="text-ink-3">—</span>),
    },
    { key: 'created_at', header: 'Date', sortable: true, cell: (r) => fmtDate(r.created_at) },
    { key: 'actions', header: '', cell: (r) => <InlineModeration review={r} /> },
  ];
  return (
    <ListScreen
      stats={<ReviewStats />}
      tabs={<ReviewTypeTabs />}
      actions={<SortMenu fields={REVIEW_SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={REVIEW_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No reviews found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const SessionReviewList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <ReviewsListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

const SessionReviewShowContent = () => {
  const { record, isLoading } = useShowContext<SessionReview>();
  const { setData } = useBreadcrumbContext();
  const [deleteOne] = useDelete();
  const [removeOpen, setRemoveOpen] = React.useState(false);

  React.useEffect(() => {
    if (record) {
      setData({
        resourceId: record.id.toString(),
        resourceName: `Review #${record.id}`,
        resourceType: 'session',
      });
    }
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading review…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Review not found</div>;

  const flagged = isFlagged(record.rating);

  const remove = () =>
    deleteOne(
      'session-reviews',
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          toastSuccess('Review removed');
          window.location.href = '/#/session-reviews';
        },
        onError: (e: any) => toastError(e?.message || 'Failed to remove review'),
        mutationMode: 'pessimistic',
      },
    );

  return (
    <div className="space-y-6 p-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
              Review #{record.id}
            </h1>
            <Stars value={record.rating} />
          </div>
          <p className="mt-1 text-sm text-ink-2">
            {typeLabel(record.review_type)}
            {record.session_title && ` · ${record.session_title}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toastInfo('Review kept')}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
          >
            Keep
          </button>
          <button
            onClick={() => setRemoveOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Trash2 className="size-4" />
            Remove
          </button>
        </div>
      </div>

      {flagged && (
        <div className="flex items-start gap-2 rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <Flag className="mt-0.5 size-4 shrink-0" />
          <span>
            <strong>Flagged for moderation.</strong> This is a low rating ({record.rating}★) and may need review.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <DetailCard title="Review" icon={MessageSquare}>
            <div className="mb-3 flex items-center gap-3">
              <Stars value={record.rating} />
              <span className="text-sm font-semibold text-ink">{record.rating}/5</span>
            </div>
            {record.comment ? (
              <blockquote className="rounded-input border-l-2 border-indigo bg-surface-2 px-4 py-3 text-sm italic text-ink">
                "{record.comment}"
              </blockquote>
            ) : (
              <p className="text-sm text-ink-2">No written comment.</p>
            )}
          </DetailCard>

          <DetailCard title="Class" icon={Calendar}>
            <div className="divide-y divide-hairline">
              <Fact label="Title" value={record.session_title || '—'} />
              <Fact
                label="Class"
                value={
                  <Link to={`/sessions/${record.session_id}/show`} className="text-indigo hover:text-indigo-press">
                    #{record.session_id}
                  </Link>
                }
              />
            </div>
          </DetailCard>
        </div>

        <div className="space-y-6">
          <DetailCard title="Reviewer" icon={User}>
            <div className="divide-y divide-hairline">
              <Fact label="Name" value={record.reviewer_name || '—'} />
              <Fact label="Email" value={record.reviewer_email || '—'} />
            </div>
          </DetailCard>

          <DetailCard title="Reviewed" icon={User}>
            <div className="divide-y divide-hairline">
              <Fact label="Name" value={record.reviewed_user_name || '—'} />
              <Fact label="Email" value={record.reviewed_user_email || '—'} />
            </div>
          </DetailCard>

          <DetailCard title="Meta">
            <div className="divide-y divide-hairline">
              <Fact label="Type" value={typeLabel(record.review_type)} />
              <Fact label="Created" value={record.created_at ? new Date(record.created_at).toLocaleString() : '—'} />
            </div>
          </DetailCard>
        </div>
      </div>

      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        tone="destructive"
        title={`Remove review #${record.id}?`}
        consequence="This permanently removes the review. This cannot be undone."
        confirmLabel="Remove review"
        onConfirm={remove}
      />
    </div>
  );
};

export const SessionReviewShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <SessionReviewShowContent />
  </Show>
);
